import { DEFAULT_LOCALE as DEFAULT_LOCALE_KEY, LOCALES } from '@/i18n/routing';
import type { Locale } from 'next-intl';
import { isSupportedLocale } from './locale-domain';

const DEFAULT_LOCALE = DEFAULT_LOCALE_KEY as Locale;
const LOCALE_PREFIX_REGEX = new RegExp(
  `^/(${LOCALES.map((locale) => escapeRegExp(locale)).join('|')})(?=/|$)`
);

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizePathname(pathname: string): string {
  if (!pathname) return '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function getLocaleFromPathname(pathname: string): Locale | null {
  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(LOCALE_PREFIX_REGEX);
  if (match && isSupportedLocale(match[1])) {
    return match[1];
  }

  return null;
}

export function getLocalePrefix(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(LOCALE_PREFIX_REGEX);
  if (!match || !isSupportedLocale(match[1])) return '';
  return `/${match[1]}`;
}

export function resolveLocaleFromPathnameOrDefault(pathname: string): Locale {
  return getLocaleFromPathname(pathname) ?? DEFAULT_LOCALE;
}

export function stripLocalePrefix(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const match = normalizedPathname.match(LOCALE_PREFIX_REGEX);
  if (!match) {
    return normalizedPathname;
  }

  const strippedPathname = normalizedPathname.slice(match[0].length);
  return strippedPathname || '/';
}

export function matchesRoutePrefix(pathname: string, route: string): boolean {
  const normalizedPathname = normalizePathname(pathname);
  const normalizedRoute = normalizePathname(route);

  return (
    normalizedPathname === normalizedRoute ||
    normalizedPathname.startsWith(`${normalizedRoute}/`)
  );
}

export function buildPathWithLocale(
  pathnameWithoutLocale: string,
  locale: Locale
): string {
  const normalizedPathname = stripLocalePrefix(pathnameWithoutLocale);

  if (locale === DEFAULT_LOCALE) {
    return normalizedPathname;
  }

  if (normalizedPathname === '/') {
    return `/${locale}`;
  }

  return `/${locale}${normalizedPathname}`;
}

export function buildLocalePathFromCurrentPath(params: {
  currentPath: string;
  locale: Locale;
}) {
  const currentPath = params.currentPath || '/';
  const suffixStart = currentPath.search(/[?#]/);
  const pathname =
    suffixStart === -1 ? currentPath : currentPath.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? '' : currentPath.slice(suffixStart);

  return `${buildPathWithLocale(pathname, params.locale)}${suffix}`;
}
